import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GridModule, GridDataResult } from '@progress/kendo-angular-grid';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import { BomDifference, BomDifferenceType } from '../../models/file-vault.models';

export interface CompareSelection {
  partId?: string;
  fromRevision?: string;
  toRevision?: string;
  fromVersionId?: string;
  toVersionId?: string;
}

@Component({
  selector: 'fv-bom-compare',
  standalone: true,
  template: `
    <div class="bom-compare-container">
      <!-- Toolbar -->
      <div class="toolbar">
        <div class="compare-selectors">
          <!-- Revision Mode -->
          <ng-container *ngIf="compareMode === 'revision'">
            <div class="selector-group">
              <label>From Revision:</label>
              <kendo-dropdownlist
                [(ngModel)]="fromRevision"
                [data]="availableRevisions"
                [valuePrimitive]="true"
                (valueChange)="onSelectionChange()">
              </kendo-dropdownlist>
            </div>
            <span class="compare-arrow">→</span>
            <div class="selector-group">
              <label>To Revision:</label>
              <kendo-dropdownlist
                [(ngModel)]="toRevision"
                [data]="availableRevisions"
                [valuePrimitive]="true"
                (valueChange)="onSelectionChange()">
              </kendo-dropdownlist>
            </div>
          </ng-container>

          <!-- Version Mode -->
          <ng-container *ngIf="compareMode === 'version'">
            <div class="selector-group">
              <label>From Version:</label>
              <kendo-dropdownlist
                [(ngModel)]="fromVersionId"
                [data]="availableVersions"
                [textField]="'label'"
                [valueField]="'id'"
                (valueChange)="onSelectionChange()">
              </kendo-dropdownlist>
            </div>
            <span class="compare-arrow">→</span>
            <div class="selector-group">
              <label>To Version:</label>
              <kendo-dropdownlist
                [(ngModel)]="toVersionId"
                [data]="availableVersions"
                [textField]="'label'"
                [valueField]="'id'"
                (valueChange)="onSelectionChange()">
              </kendo-dropdownlist>
            </div>
          </ng-container>
        </div>

        <div class="toolbar-actions">
          <button kendoButton [themeColor]="'primary'" (click)="compare()" [disabled]="!canCompare || isLoading">
            Compare
          </button>
          <button kendoButton [look]="'flat'" (click)="swap()" [disabled]="!canSwap">
            Swap
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div class="loading" *ngIf="isLoading">
        <kendo-loader [type]="'infinite-spinner'" [size]="'medium'"></kendo-loader>
        <span>Comparing BOMs...</span>
      </div>

      <!-- No Comparison Yet -->
      <div class="empty" *ngIf="!isLoading && !hasCompared">
        <div class="empty-icon">⚖️</div>
        <div class="empty-text">Select revisions to compare</div>
        <div class="empty-hint">Choose a "From" and "To" revision, then click Compare</div>
      </div>

      <!-- No Differences -->
      <div class="no-differences" *ngIf="!isLoading && hasCompared && differences.length === 0">
        <div class="check-icon">✓</div>
        <div class="message">BOMs are identical</div>
        <div class="hint">No differences found between the selected revisions</div>
      </div>

      <!-- Differences Summary -->
      <div class="summary" *ngIf="!isLoading && differences.length > 0">
        <div class="summary-item added">
          <span class="count">{{ addedCount }}</span>
          <span class="label">Added</span>
        </div>
        <div class="summary-item removed">
          <span class="count">{{ removedCount }}</span>
          <span class="label">Removed</span>
        </div>
        <div class="summary-item changed">
          <span class="count">{{ changedCount }}</span>
          <span class="label">Changed</span>
        </div>
      </div>

      <!-- Differences Grid -->
      <kendo-grid
        *ngIf="!isLoading && differences.length > 0"
        [data]="gridData"
        [sortable]="true"
        [groupable]="true"
        [pageSize]="50"
        [pageable]="differences.length > 50"
        class="differences-grid">

        <kendo-grid-column field="differenceType" title="Change" [width]="100">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="diff-badge" [class]="getDiffClass(dataItem.differenceType)">
              {{ getDiffLabel(dataItem.differenceType) }}
            </span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="partNumber" title="Part Number" [width]="150">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="part-number">{{ dataItem.part?.partNumber || '-' }}</span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="partName" title="Name" [width]="200">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.part?.name || '-' }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="quantity" title="Quantity" [width]="150">
          <ng-template kendoGridCellTemplate let-dataItem>
            <ng-container *ngIf="hasQuantityChange(dataItem)">
              <span class="old-value">{{ dataItem.previousQuantity }}</span>
              <span class="change-arrow">→</span>
              <span class="new-value">{{ dataItem.newQuantity }}</span>
            </ng-container>
            <span *ngIf="!hasQuantityChange(dataItem)">-</span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="revision" title="Revision" [width]="150">
          <ng-template kendoGridCellTemplate let-dataItem>
            <ng-container *ngIf="hasRevisionChange(dataItem)">
              <span class="old-value">{{ dataItem.previousRevision || '-' }}</span>
              <span class="change-arrow">→</span>
              <span class="new-value">{{ dataItem.newRevision || '-' }}</span>
            </ng-container>
            <span *ngIf="!hasRevisionChange(dataItem)">-</span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="findNumber" title="Find #" [width]="120">
          <ng-template kendoGridCellTemplate let-dataItem>
            <ng-container *ngIf="hasFindNumberChange(dataItem)">
              <span class="old-value">{{ dataItem.previousFindNumber || '-' }}</span>
              <span class="change-arrow">→</span>
              <span class="new-value">{{ dataItem.newFindNumber || '-' }}</span>
            </ng-container>
            <span *ngIf="!hasFindNumberChange(dataItem)">-</span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="description" title="Description">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.description }}
          </ng-template>
        </kendo-grid-column>
      </kendo-grid>
    </div>
  `,
  styles: [`
    .bom-compare-container {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px;
      background: var(--fv-surface-hover);
      border-bottom: 1px solid var(--fv-border);
      flex-wrap: wrap;
      gap: 12px;
    }

    .compare-selectors {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
    }

    .selector-group {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .selector-group label {
      font-size: 11px;
      color: var(--fv-text-secondary);
      font-weight: 500;
    }

    .compare-arrow {
      font-size: 20px;
      color: var(--fv-text-secondary);
      margin-top: 16px;
    }

    .toolbar-actions {
      display: flex;
      gap: 8px;
    }

    .loading {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 48px;
      gap: 12px;
      color: var(--fv-text-secondary);
    }

    .empty, .no-differences {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 48px;
      color: var(--fv-text-secondary);
    }

    .empty-icon {
      font-size: 48px;
      margin-bottom: 12px;
    }

    .empty-text, .message {
      font-size: 16px;
      font-weight: 500;
    }

    .empty-hint, .hint {
      font-size: 13px;
      color: var(--fv-text-muted);
      margin-top: 4px;
    }

    .check-icon {
      font-size: 48px;
      color: var(--fv-status-success-fg);
      margin-bottom: 12px;
    }

    .summary {
      display: flex;
      gap: 24px;
      padding: 12px 16px;
      background: var(--fv-surface-alt);
      border-bottom: 1px solid var(--fv-border);
    }

    .summary-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
    }

    .summary-item .count {
      font-size: 24px;
      font-weight: 600;
    }

    .summary-item .label {
      font-size: 11px;
      text-transform: uppercase;
      color: var(--fv-text-secondary);
    }

    .summary-item.added .count { color: var(--fv-status-success-fg); }
    .summary-item.removed .count { color: var(--fv-status-error-fg); }
    .summary-item.changed .count { color: var(--fv-status-warning-fg); }

    .differences-grid {
      flex: 1;
      font-size: 13px;
    }

    .part-number {
      font-family: monospace;
      font-weight: 500;
    }

    .diff-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 500;
      text-transform: uppercase;
    }

    .diff-badge.added {
      background: var(--fv-status-success-bg);
      color: var(--fv-status-success-fg);
    }

    .diff-badge.removed {
      background: var(--fv-status-error-bg);
      color: var(--fv-status-error-fg);
    }

    .diff-badge.changed {
      background: var(--fv-status-warning-bg);
      color: var(--fv-status-warning-fg);
    }

    .old-value {
      color: var(--fv-text-muted);
      text-decoration: line-through;
    }

    .change-arrow {
      margin: 0 4px;
      color: var(--fv-text-secondary);
    }

    .new-value {
      font-weight: 500;
    }
  `],
  imports: [CommonModule, FormsModule, GridModule, ButtonsModule, DropDownsModule, IndicatorsModule]
})
export class BomCompareComponent implements OnInit, OnChanges {
  private api = inject(FileVaultApiService);

  /** Part ID to compare BOMs for */
  @Input() partId?: string;

  /** Part number to compare BOMs for (resolved to Part ID) */
  @Input() partNumber?: string;

  /** Compare mode: by revision string or by version ID */
  @Input() compareMode: 'revision' | 'version' = 'revision';

  /** Available revisions for selection */
  @Input() availableRevisions: string[] = [];

  /** Available versions for selection */
  @Input() availableVersions: { id: string; label: string }[] = [];

  /** Pre-selected from revision */
  @Input() fromRevision?: string;

  /** Pre-selected to revision */
  @Input() toRevision?: string;

  /** Pre-selected from version ID */
  @Input() fromVersionId?: string;

  /** Pre-selected to version ID */
  @Input() toVersionId?: string;

  /** Emitted when comparison is complete */
  @Output() compared = new EventEmitter<BomDifference[]>();

  differences: BomDifference[] = [];
  isLoading = false;
  hasCompared = false;
  private resolvedPartId?: string;

  get gridData(): GridDataResult {
    return {
      data: this.differences,
      total: this.differences.length
    };
  }

  get canCompare(): boolean {
    if (this.compareMode === 'revision') {
      return !!this.fromRevision && !!this.toRevision && this.fromRevision !== this.toRevision;
    }
    return !!this.fromVersionId && !!this.toVersionId && this.fromVersionId !== this.toVersionId;
  }

  get canSwap(): boolean {
    if (this.compareMode === 'revision') {
      return !!this.fromRevision && !!this.toRevision;
    }
    return !!this.fromVersionId && !!this.toVersionId;
  }

  get addedCount(): number {
    return this.differences.filter(d => d.differenceType === BomDifferenceType.Added).length;
  }

  get removedCount(): number {
    return this.differences.filter(d => d.differenceType === BomDifferenceType.Removed).length;
  }

  get changedCount(): number {
    return this.differences.filter(d =>
      d.differenceType !== BomDifferenceType.Added &&
      d.differenceType !== BomDifferenceType.Removed
    ).length;
  }

  ngOnInit(): void {
    // Auto-compare if both values are pre-selected
    if (this.canCompare) {
      this.compare();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reset comparison when part changes
    if ((changes['partId'] || changes['partNumber']) && !changes['partId']?.firstChange) {
      this.differences = [];
      this.hasCompared = false;
    }
  }

  onSelectionChange(): void {
    // Could auto-compare on change, but typically wait for explicit action
  }

  async compare(): Promise<void> {
    if (!this.canCompare) return;

    this.isLoading = true;
    this.differences = [];

    try {
      let diffs: BomDifference[];
      const currentPartId = await this.getCurrentPartId();

      if (this.compareMode === 'revision') {
        if (!currentPartId) return;
        diffs = await this.api.compareBomByRevision(
          currentPartId,
          this.fromRevision!,
          this.toRevision!
        ).toPromise() ?? [];
      } else {
        diffs = await this.api.compareBomByVersion(
          this.fromVersionId!,
          this.toVersionId!
        ).toPromise() ?? [];
      }

      this.differences = diffs;
      this.hasCompared = true;
      this.compared.emit(diffs);
    } catch (error) {
      console.error('Failed to compare BOMs', error);
      this.differences = [];
    } finally {
      this.isLoading = false;
    }
  }

  swap(): void {
    if (this.compareMode === 'revision') {
      const temp = this.fromRevision;
      this.fromRevision = this.toRevision;
      this.toRevision = temp;
    } else {
      const temp = this.fromVersionId;
      this.fromVersionId = this.toVersionId;
      this.toVersionId = temp;
    }
  }

  getDiffLabel(type: BomDifferenceType): string {
    const labels: Record<number, string> = {
      [BomDifferenceType.Added]: 'Added',
      [BomDifferenceType.Removed]: 'Removed',
      [BomDifferenceType.QuantityChanged]: 'Qty Changed',
      [BomDifferenceType.RevisionChanged]: 'Rev Changed',
      [BomDifferenceType.FindNumberChanged]: 'Find # Changed',
      [BomDifferenceType.Multiple]: 'Multiple'
    };
    return labels[type] ?? 'Changed';
  }

  getDiffClass(type: BomDifferenceType): string {
    if (type === BomDifferenceType.Added) return 'added';
    if (type === BomDifferenceType.Removed) return 'removed';
    return 'changed';
  }

  hasQuantityChange(diff: BomDifference): boolean {
    return diff.differenceType === BomDifferenceType.QuantityChanged ||
           diff.differenceType === BomDifferenceType.Multiple ||
           (diff.previousQuantity !== undefined && diff.newQuantity !== undefined);
  }

  hasRevisionChange(diff: BomDifference): boolean {
    return diff.differenceType === BomDifferenceType.RevisionChanged ||
           diff.differenceType === BomDifferenceType.Multiple ||
           (diff.previousRevision !== undefined || diff.newRevision !== undefined);
  }

  hasFindNumberChange(diff: BomDifference): boolean {
    return diff.differenceType === BomDifferenceType.FindNumberChanged ||
           diff.differenceType === BomDifferenceType.Multiple ||
           (diff.previousFindNumber !== undefined || diff.newFindNumber !== undefined);
  }

  private async getCurrentPartId(): Promise<string | undefined> {
    if (this.partId) {
      this.resolvedPartId = this.partId;
      return this.partId;
    }

    if (this.resolvedPartId && this.partNumber === undefined) {
      return this.resolvedPartId;
    }

    if (!this.partNumber) return undefined;

    try {
      const part = await this.api.getPartByNumber(this.partNumber).toPromise();
      if (part?.id) {
        this.resolvedPartId = part.id;
        return part.id;
      }
    } catch (error) {
      console.error('Failed to resolve part number', error);
    }

    return undefined;
  }
}
